<template>
    <page-content
        v-if="!prerenderMode && typeof product === 'object' && Object.values(product).length > 0"
        :key="product.id"
    >
        <images-and-videos slot="imagesAndVideos" />
        <product-title slot="productTitle" ref="titleReference" />
        <product-sub-title slot="productSubTitle" />
        <average-rating
            :showReviews="product.reviewCount > 0"
            :rating="product.rating"
            :reviewCount="product.reviewCount"
            slot="averageRating"
        />
        <product-short-description slot="productShortDescription" />
        <product-long-description slot="productLongDescription" />

        <product-long-description
            :attributes="getShortDescriptionAttributes()"
            :additions="product.attributesAdditions"
            cols="1"
            slot="productLongDescriptionShortened"
            shortened
        />
        <product-sidebar v-if="pricesLoaded" :product="product" slot="productSidebar" ref="sidebar" />

        <load-bee
            slot="loadbee"
            :product-id="product.attributes.de['mb.gtin']"
            :apikey="$config.LOADBEE_API_KEY"
            :locale="$config.LOADBEE_LOCALE"
            :debug="$config.LOADBEE_DEBUG"
        ></load-bee>

        <nexmart
            slot="nextmart"
            :product-id="product.attributes.de['product.manufacturer.id']"
            :partner-key="$config.NEXTMART_PARTNER_KEY"
            :supplier="$config.NEXTMART_SUPPLIER"
            :lang="$config.NEXTMART_LANGUAGE"
        ></nexmart>

        <product-specification
            :attributes="product.public_attributes"
            :additions="product.attributesAdditions"
            slot="productSpecificationShort"
            :limit="4"
            text-color="primary"
        />
        <product-specification
            :attributes="getProductAttributesExcept()"
            :additions="product.attributesAdditions"
            slot="productSpecification"
            cols="2"
        />

        <technical-info
            slot="technicalInfo"
            :attributes="getTechnicalAttributes()"
            :additions="product.attributesAdditions"
            v-bind:hide-labels="true"
            cols="1"
        />

        <product-info
            slot="productInfo"
            :attributes="getInfoAttributes()"
            :additions="product.attributesAdditions"
            cols="2"
        />
        <warranty-data
            slot="warrantyData"
            :attributes="getWarrantyAttributes()"
            :additions="product.attributesAdditions"
            v-bind:hide-labels="true"
            cols="1"
        />

        <buy-options :buy-options="product.buyOptions" :product="product" slot="buyOptions" />
        <upsells type="upsells" slot="upsells" />
        <crosssells type="crosssells" slot="crosssells" />
        <product-reviews
            v-if="product.reviews && product.reviews.length"
            :reviews="product.reviews"
            slot="productReviews"
        />
        <product-review-card v-if="highlightedReview" :review="highlightedReview" slot="productHighlightedReview" />
        <view-statistics slot="viewStatistics" />
        <product-video slot="productVideo" />
        <info :product="product" slot="customersInfo" />
    </page-content>

    <page-not-found
        :customName="notFoundPage"
        message="Product not found"
        v-else-if="!prerenderMode && component404IsVisible"
    />
</template>

<script async src="https://cdn.loadbee.com/js/loadbee_integration.js"></script>

<script>
import Vue from 'vue';
// import ProductLongDescription from './product-page/ProductLongDescription';
// import ProductSpecification from './product-page/ProductSpecification';
import ProductVariants from './product-page/ProductVariants';
import AmountInput from './product-page/AmountInput';
import BasketButton from './product-page/BasketButton';
import SharingButtons from './product-page/SharingButtons';
import ProductTemplate from './ProductTemplate';
import { mapState, mapActions, createNamespacedHelpers } from 'vuex';
import { eventManager } from '@breakmedia/prime-vue-event-manager';
import ProductPlaceholderTemplate from './ProductPlaceholderTemplate';
import BuyOptions from './product-page/BuyOptions';
import ViewStatistics from './product-page/ViewStatistics';
import ProductVideo from './product-page/ImagesAndVideos/ProductVideo';
import { Observable } from 'rxjs';
import { getProducts } from './store/getProducts';
import { PRODUCT_FIELDS } from './constants/productFields';
import ProductReviewCard from './product-page/Reviews/ProductReviewCard';

import Info from './Info';
import TechnicalInfo from './TechnicalInfo';
import ProductInfo from './ProductInfo';
import WarrantyData from './WarrantyData';
import ProductLongDescription from './ProductLongDescription';
import ProductSpecification from './ProductSpecification';
import LoadBee from './Loadbee';
import Nexmart from './Nexmart';

const moduleName = 'Product';
const { mapGetters, mapMutations } = createNamespacedHelpers(moduleName);

const params = {
    fields: PRODUCT_FIELDS,
};

let paramsSubscriber;
let productSubscriber;
let loadedProduct = {};
let ownLayoutPromiseResolve;

let sameProduct = false;

export default {
    name: moduleName,
    components: {
        LoadBee,
        Nexmart,
        WarrantyData,
        ProductInfo,
        TechnicalInfo,
        ProductLongDescription,
        ProductSpecification,
        ProductVariants,
        AmountInput,
        BasketButton,
        SharingButtons,
        BuyOptions,
        ViewStatistics,
        ProductVideo,
        ProductReviewCard,
        Info,
    },
    data() {
        return {
            notFoundPage: '404-product.html',
            metaTitle: '',
            metaDescription: '',
        };
    },
    getOwnLayout(route) {
        Vue.$store.commit(`${moduleName}/setPricesLoading`, false);
        Vue.$store.commit(`${moduleName}/setPricesLoaded`, false);
        sameProduct = false;
        Vue.$store.dispatch('PageContent/switchTemplate', {
            name: ProductPlaceholderTemplate,
        });

        if (paramsSubscriber) {
            paramsSubscriber.next({
                ...params,
                ids: [route.params.id.toString()],
            });
        }

        if (!productSubscriber) {
            productSubscriber = getProducts(
                new Observable(subscriber => {
                    paramsSubscriber = subscriber;
                    if (route.params.id) {
                        subscriber.next({
                            skipPriceParams: true,
                            ...params,
                            ids: [route.params.id.toString()],
                        });
                    } else {
                        Vue.nextTick(() => {
                            ownLayoutPromiseResolve({
                                fallback: ProductTemplate,
                            });
                        });
                    }
                }),
            ).subscribe(products => {
                if (products && products.length) {
                    let product = products[0];
                    if (!Vue.$store.state[moduleName].pricesLoading) {
                        let layout;
                        let template = route.meta.template ? route.meta.template : ProductTemplate;

                        if (
                            product &&
                            product.hasOwnProperty('isNotBuyable') &&
                            product.isNotBuyable[route.params.id.toString()] === true
                        ) {
                            Vue.$store.commit(`${moduleName}/setProduct`, {});
                            ownLayoutPromiseResolve({});
                        }

                        if (product) {
                            loadedProduct = product;
                            if (product.attributes.de.product_page_layout) {
                                layout = product.attributes.de.product_page_layout;
                            }
                            if (product.attributes.de.product_page_template) {
                                template = product.attributes.de.product_page_template;
                            }
                        } else {
                            loadedProduct = {};
                        }

                        ownLayoutPromiseResolve({
                            layout,
                            template,
                            fallback: ProductTemplate,
                        });
                    } else {
                        Vue.$store.commit(`${moduleName}/setProduct`, { product, silent: true });
                        if (Vue.$store.state[moduleName].product.id == product.id) {
                            sameProduct = true;
                        }
                        Vue.$store.commit(`${moduleName}/setPricesLoaded`, true);
                    }
                } else {
                    ownLayoutPromiseResolve({
                        fallback: ProductTemplate,
                    });
                }
            });
        }

        return new Promise(resolve => {
            ownLayoutPromiseResolve = resolve;
        });
    },
    beforeDestroy() {
        productSubscriber ? productSubscriber.unsubscribe() : null;
        paramsSubscriber = null;
        productSubscriber = null;
        this.setPricesLoading(false);
        this.setPricesLoaded(false);
    },
    destroyed() {
        this.update404ComponentVisibility(false);
    },
    onLayoutReady(route) {
        if (Vue.$store.state.PageContent.page && Vue.$store.state.PageContent.page.hasOwnProperty('attributes')) {
            this.metaDescription = Vue.$store.state.PageContent.page.attributes.page_description;
        }

        Vue.$store.commit(`${moduleName}/setProduct`, { product: loadedProduct });
        loadedProduct = {};

        if (this.product) {
            Vue.$store.dispatch('PageNotFound/updateVisibility', false);
        } else {
            Vue.$store.dispatch('PageNotFound/updateVisibility', true);
        }

        Vue.$store.commit(`${moduleName}/setCurrentMerchantId`, route.params.merchant);
        Vue.$store.commit(`${moduleName}/setCurrentLinkMerchant`, route.params.merchant);
    },
    metaInfo() {
        const title = this.getProductAttributeByLanguage(this.product, 'seo_meta_name')
            ? this.getProductAttributeByLanguage(this.product, 'seo_meta_name')
            : this.getProductAttributeByLanguage(this.product, 'product.name');
        const metaDescription = this.getProductAttributeByLanguage(this.product, 'product.description.long')
            .replace(/<[^>]*>?/gm, '')
            .substring(0, 250);
        const url = this.getProductAttributeByLanguage(this.product, 'product.url');

        let brand = this.getProductAttributeByLanguage(this.product, 'mb.brand');
        if (brand && typeof brand === 'object') {
            brand = Object.values(brand)[0];
        }
        let currentMerchant;
        if (this.currentMerchantId) {
            currentMerchant = this.product.merchant[this.currentMerchantId];
        }
        let productCategory;
        if (this.product.categories && this.product.categories.length) {
            this.product.categories.some(categoryId => {
                if (this.categories[categoryId]) {
                    productCategory = this.categories[categoryId].label;
                    return true;
                }
            });
        }

        let price = null;
        if (
            currentMerchant &&
            this.currentProductId &&
            currentMerchant.price &&
            currentMerchant.price[this.currentProductId]
        ) {
            price = currentMerchant.price[this.currentProductId].price;
        } else if (this.product && this.product.price) {
            price = this.product.price.price;
        }
        return {
            title: title,
            meta: [
                {
                    vmid: 'description',
                    name: 'description',
                    content: this.getProductAttributeByLanguage(this.product, 'seo_meta_description')
                        ? this.getProductAttributeByLanguage(this.product, 'seo_meta_description')
                        : metaDescription
                        ? metaDescription
                        : this.metaDescription,
                },
                {
                    vmid: 'keywords',
                    name: 'keywords',
                    content: this.getProductAttributeByLanguage(this.product, 'seo_search_terms'),
                },
                {
                    vmid: 'og:title',
                    name: 'og:title',
                    content: title,
                },
                {
                    vmid: 'og:description',
                    name: 'og:description',
                    content: this.getProductAttributeByLanguage(this.product, 'seo_meta_description')
                        ? this.getProductAttributeByLanguage(this.product, 'seo_meta_description')
                        : metaDescription
                        ? metaDescription
                        : this.metaDescription,
                },
                {
                    vmid: 'og:url',
                    name: 'og:url',
                    content: `${location.origin}/${url}`,
                },
                {
                    vmid: 'og:image',
                    name: 'og:image',
                    content: this.product.images.product_images[0]
                        ? `${location.origin}/${this.product.images.product_images[0].big}`
                        : '',
                },
                {
                    vmid: 'og:locale',
                    name: 'og:locale',
                    content: Vue.$language.getLanguage(),
                },
                {
                    vmid: 'og:price:amount',
                    name: 'og:price:amount',
                    content: price,
                },
                {
                    vmid: 'og:price:currency',
                    name: 'og:price:currency',
                    content: this.$currency.getSelected(),
                },
                {
                    vmid: 'product:brand',
                    name: 'product:brand',
                    content: brand,
                },
                {
                    vmid: 'product:availability',
                    name: 'product:availability',
                    content: currentMerchant ? currentMerchant.quota[this.currentProductId] : '',
                },
                {
                    vmid: 'product:category',
                    name: 'product:category',
                    content: productCategory,
                },
                {
                    vmid: 'product:condition',
                    name: 'product:condition',
                    content: 'new',
                },
                {
                    vmid: 'product:gender',
                    name: 'product:gender',
                    content: this.getProductAttributeByLanguage(this.product, 'gender'),
                },
                {
                    vmid: 'product:locale',
                    name: 'product:locale',
                    content: Vue.$language.getLanguage(),
                },
                {
                    vmid: 'product:price:amount',
                    name: 'product:price:amount',
                    content: price,
                },
                {
                    vmid: 'product:price:currency',
                    name: 'product:price:currency',
                    content: this.$currency.getSelected(),
                },
                {
                    vmid: 'product:retailer_item_id',
                    name: 'product:retailer_item_id',
                    content: this.product.ext_id,
                },
            ],
            __dangerouslyDisableSanitizersByTagID: {
                description: ['content'],
            },
            script: [
                {
                    type: 'application/ld+json',
                    json: {
                        '@context': 'http://schema.org/',
                        '@type': 'Product',
                        name: title,
                        image: this.product.images.product_images[0]
                            ? `${location.origin}/${this.product.images.product_images[0].big}`
                            : '',
                        description: this.getProductAttributeByLanguage(this.product, 'product.description.short')
                            ? this.getProductAttributeByLanguage(this.product, 'product.description.short')
                            : this.getProductAttributeByLanguage(this.product, 'product.description.long'),
                        brand: brand,
                        sku: this.product.ext_id,
                        gtin13: this.getProductAttributeByLanguage(this.product, 'mb.gtin'),
                        offers: {
                            '@type': 'Offer',
                            priceCurrency: this.$currency.getSelected(),
                            price: currentMerchant
                                ? currentMerchant.price[this.currentProductId].price
                                : this.product.price.price,
                            availability: this.isBuyable ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
                            itemCondition: 'http://schema.org/NewCondition',
                            url: `${location.origin}/product/${this.currentProductId}`,
                        },
                    },
                },
            ],
        };
    },
    computed: {
        ...mapState(moduleName, [
            'product',
            'currentProductId',
            'currentMerchantId',
            'amountValue',
            'pricesLoading',
            'pricesLoaded',
        ]),
        ...mapState('PageNotFound', {
            component404IsVisible: state => state.isVisible,
        }),
        ...mapState('SearchFilters', ['categories']),
        ...mapState('PageContent', ['prerenderMode']),
        ...mapGetters(['getProductAttributeByLanguage', 'isProductBuyable']),
        highlightedReview() {
            if (this.product.reviews) {
                let highlightedReviews = this.product.reviews.filter(r => r.highlighted);

                if (highlightedReviews.length) {
                    return highlightedReviews[0];
                }
            }
        },
        isBuyable() {
            return this.isProductBuyable(this.product, this.currentMerchantId, this.currentProductId);
        },
        titleRef() {
            return this.$refs.titleReference;
        },
    },
    methods: {
        ...mapActions('PageNotFound', {
            update404ComponentVisibility: 'updateVisibility',
        }),
        ...mapMutations(['setCurrentMerchantId', 'setCurrentLinkMerchant', 'setPricesLoading', 'setPricesLoaded']),
        getInfoAttributes() {
            const lang = Vue.$language.getLanguage();
            return {
                [lang]: this.getProductAttributes(this.product, ['mb.brand']),
            };
        },
        getTechnicalAttributes() {
            const lang = Vue.$language.getLanguage();
            return {
                [lang]: this.getProductAttributes(this.product, ['product.technical.details']),
            };
        },
        getWarrantyAttributes() {
            const lang = Vue.$language.getLanguage();
            return {
                [lang]: this.getProductAttributes(this.product, ['product.mandatory.text']),
            };
        },
        getDescriptionAttributes() {
            const lang = Vue.$language.getLanguage();
            return {
                [lang]: this.getProductAttributesExcept(this.product, [
                    'mb.brand',
                    'product.technical.details',
                    'product.mandatory.text',
                ]),
            };
        },
        getShortDescriptionAttributes() {
            const lang = Vue.$language.getLanguage();
            return {
                [lang]: this.getProductAttributes(this.product, ['mb.sku']),
            };
        },
        getProductAttributes(product, filterAttributes) {
            const lang = Vue.$language.getLanguage();
            let attributes = {};
            if (product && product.public_attributes) {
                if (lang && product.public_attributes.hasOwnProperty(lang)) {
                    attributes = product.public_attributes[lang];
                } else {
                    attributes = Object.values(product.public_attributes)[0];
                }
            }
            if (attributes && filterAttributes) {
                return Object.keys(attributes)
                    .filter(attribute => filterAttributes.includes(attribute))
                    .reduce((obj, key) => {
                        obj[key] = attributes[key];
                        return obj;
                    }, {});
            } else {
                return attributes;
            }
        },
        getProductAttributesExcept(product, filterAttributes) {
            const lang = Vue.$language.getLanguage();
            let attributes = {};
            if (product && product.public_attributes) {
                if (lang && product.public_attributes.hasOwnProperty(lang)) {
                    attributes = product.public_attributes[lang];
                } else {
                    attributes = Object.values(product.public_attributes)[0];
                }
            }
            if (attributes && filterAttributes) {
                return Object.keys(attributes)
                    .filter(attribute => !filterAttributes.includes(attribute))
                    .reduce((obj, key) => {
                        obj[key] = attributes[key];
                        return obj;
                    }, {});
            } else {
                return attributes;
            }
        },
    },
    watch: {
        product: {
            handler(newProduct, oldProduct) {
                if (newProduct) {
                    if (newProduct.price && newProduct.price.price) {
                        eventManager.$emit('productView', {
                            product: {
                                ...newProduct,
                                catalog_item_id: this.$route.params.id,
                            },
                            eventId: eventManager.getUniqueId('productView'),
                        });
                    }
                    if (!this.pricesLoading && !sameProduct) {
                        this.setPricesLoading(true);
                        paramsSubscriber.next({
                            ...params,
                            ids: [this.$route.params.id],
                            skipPriceParams: false,
                        });
                    }
                }
            },
            immediate: true,
            deep: true,
        },
        amountValue(quantity) {
            paramsSubscriber.next({
                ...params,
                ids: [this.$route.params.id],
                priceParams: [
                    {
                        quantity: {
                            [this.$route.params.id]: quantity,
                        },
                    },
                ],
            });
        },
    },
};
</script>
